import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import {
  createClassAccessRequest,
  getClassesBySchoolId,
  getClassesByTeacherId,
  getTeachersBySchoolId
} from "../services/APIClient";
import {
  getSupportRequestErrorMessage,
  hasPlaceholderTargetId,
  isDuplicatePendingResponse
} from "../utils/teacherWorkflowRequests";
import { getClassGradeLabel, getDistinctClassGradeLabel } from "../utils/classDisplay";
import "./TeacherRequestAccessModal.css";

const getClassId = (classItem) =>
  classItem?.id || classItem?._id || classItem?.classid || classItem?.classId;

const getClassOwnerId = (classItem) =>
  classItem?.classOwnerId ||
  classItem?.class_owner_id ||
  classItem?.ownerId;

const getClassCreatorId = (classItem) =>
  classItem?.teacherId ||
  classItem?.teacher_id;

const getAcademicYearFromClass = (classItem) =>
  classItem?.academicYear ||
  classItem?.academic_year ||
  classItem?.academicyear ||
  classItem?.schoolYear ||
  classItem?.yearLabel ||
  "";

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normaliseTeacher = (teacher) => ({
  id: teacher?.id || teacher?._id,
  name: teacher?.name || teacher?.fullName || teacher?.displayName || "Class teacher",
  roleLabel:
    teacher?.roleLabel ||
    teacher?.position ||
    teacher?.title ||
    teacher?.role ||
    (Array.isArray(teacher?.roles) ? teacher.roles[0] : "") ||
    "Teacher"
});

const normaliseClass = (classItem, teachersById, countryGrades) => {
  const ownerId = getClassOwnerId(classItem);
  const creatorId = getClassCreatorId(classItem);
  const teacherId = ownerId || creatorId;
  const ownerName =
    teachersById.get(teacherId)?.name ||
    classItem?.teacherName ||
    classItem?.ownerName ||
    classItem?.classOwnerName ||
    "Class teacher";
  const academicYear = getAcademicYearFromClass(classItem);
  const grade = getClassGradeLabel(classItem, countryGrades);
  const name = classItem?.name || classItem?.className || "Unnamed Class";
  const detailLabel = [getDistinctClassGradeLabel(classItem, name, countryGrades), ownerName, academicYear]
    .filter(Boolean)
    .join(", ");

  return {
    ...classItem,
    id: getClassId(classItem),
    name,
    ownerId,
    creatorId,
    teacherId,
    ownerName,
    academicYear,
    grade,
    detailLabel,
    searchText: [
      classItem?.name,
      classItem?.className,
      ownerName,
      academicYear,
      grade
    ].filter(Boolean).join(" ").toLowerCase()
  };
};

const normalisePrefill = (prefill) => ({
  className: String(prefill?.className || prefill?.class_name || "").trim(),
  teacherName: String(prefill?.teacherName || prefill?.teacher_name || "").trim(),
  secondTeacherName: String(
    prefill?.secondTeacherName ||
    prefill?.second_teacher_name ||
    prefill?.classOwnerTeacherName ||
    prefill?.class_owner_teacher_name ||
    ""
  ).trim(),
  note: String(prefill?.note || "").trim()
});

const getSingleExactTeacherMatch = (teachers, teacherName) => {
  const normalisedTeacherName = normaliseText(teacherName);

  if (!normalisedTeacherName) {
    return null;
  }

  const matches = teachers.filter(
    (teacher) => normaliseText(teacher.name) === normalisedTeacherName
  );

  return matches.length === 1 ? matches[0] : null;
};

const getSingleExactClassMatch = (classes, { className, ownerName, targetTeacher }) => {
  const normalisedClassName = normaliseText(className);
  const normalisedOwnerName = normaliseText(ownerName);

  if (!normalisedClassName && !normalisedOwnerName) {
    return null;
  }

  const matches = classes.filter((classItem) => {
    const classNameMatches = !normalisedClassName || normaliseText(classItem.name) === normalisedClassName;
    const ownerMatches = !normalisedOwnerName || normaliseText(classItem.ownerName) === normalisedOwnerName;
    const targetTeacherNeedsAccess =
      !targetTeacher ||
      (classItem.ownerId !== targetTeacher.id && classItem.creatorId !== targetTeacher.id);

    return classNameMatches && ownerMatches && targetTeacherNeedsAccess;
  });

  return matches.length === 1 ? matches[0] : null;
};

const getAssociatedClassIds = (assignments) =>
  new Set(
    (Array.isArray(assignments) ? assignments : [])
      .map((assignment) => getClassId(assignment?.class || assignment?.classItem || assignment))
      .filter(Boolean)
  );

const teacherNeedsClassAccess = (classItem, targetTeacherId, associatedClassIds) =>
  Boolean(
    classItem &&
    targetTeacherId &&
    classItem.ownerId !== targetTeacherId &&
    classItem.creatorId !== targetTeacherId &&
    !associatedClassIds.has(classItem.id)
  );

function TeacherRequestAccessModal({
  show,
  handleClose,
  schoolid,
  countryGrades = null,
  teacherid,
  mode = "self",
  prefill = null,
  onOpenOppositeFlow = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [searchTerm, setSearchTerm] = useState("");
  const [teacherSearchTerm, setTeacherSearchTerm] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTeacherAccess, setIsLoadingTeacherAccess] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");
  const [isDuplicatePending, setIsDuplicatePending] = useState(false);
  const [associatedClassIds, setAssociatedClassIds] = useState(new Set());
  const [hasLoadedTargetAccess, setHasLoadedTargetAccess] = useState(false);
  const [restartCount, setRestartCount] = useState(0);
  const teacherAccessLoadId = useRef(0);

  const isOtherTeacherMode = mode === "otherTeacher";
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);
  const trimmedSearchTerm = searchTerm.trim();
  const trimmedTeacherSearchTerm = teacherSearchTerm.trim();
  const canSend = Boolean(
    selectedClass &&
    (!isOtherTeacherMode || selectedTeacher) &&
    hasLoadedTargetAccess &&
    !isLoadingTeacherAccess &&
    !isSending
  );

  useEffect(() => {
    if (!show) {
      return;
    }

    let cancelled = false;
    const activePrefillContext = restartCount > 0 ? normalisePrefill(null) : prefillContext;

    teacherAccessLoadId.current += 1;
    setStage("form");
    setSearchTerm(
      activePrefillContext.className ||
      (isOtherTeacherMode ? activePrefillContext.secondTeacherName : activePrefillContext.teacherName)
    );
    setTeacherSearchTerm(isOtherTeacherMode ? activePrefillContext.teacherName : "");
    setTeachers([]);
    setSelectedTeacher(null);
    setClasses([]);
    setSelectedClass(null);
    setNote(activePrefillContext.note);
    setError("");
    setIsDuplicatePending(false);
    setAssociatedClassIds(new Set());
    setHasLoadedTargetAccess(false);
    setIsLoadingTeacherAccess(false);

    if (!schoolid) {
      setError("Classes could not be loaded. Please try again.");
      return;
    }

    setIsLoading(true);

    const loadRequestContext = async () => {
      try {
        const [teachersResponse, classesResponse] = await Promise.all([
          getTeachersBySchoolId(schoolid),
          getClassesBySchoolId(schoolid)
        ]);
        const normalisedTeachers = (Array.isArray(teachersResponse) ? teachersResponse : [])
          .map(normaliseTeacher)
          .filter((teacher) => teacher.id)
          .sort((teacherA, teacherB) => teacherA.name.localeCompare(teacherB.name));
        const teachersById = new Map(
          normalisedTeachers.map((teacher) => [teacher.id, teacher])
        );

        const normalisedClasses = (Array.isArray(classesResponse) ? classesResponse : [])
          .map((classItem) => normaliseClass(classItem, teachersById, countryGrades))
          .filter((classItem) => classItem.id)
          .filter((classItem) => classItem.active !== false)
          .filter((classItem) => !classItem.snapshot && !classItem.trial_class)
          .sort((classA, classB) => {
            if (classA.academicYear !== classB.academicYear) {
              return classB.academicYear?.localeCompare(classA.academicYear || "") || 0;
            }

            return classA.name.localeCompare(classB.name);
          });

        const availableTeachers = normalisedTeachers.filter((teacher) => teacher.id !== teacherid);
        const prefilledTeacher = isOtherTeacherMode
          ? getSingleExactTeacherMatch(availableTeachers, activePrefillContext.teacherName)
          : null;
        const targetTeacherId = isOtherTeacherMode ? prefilledTeacher?.id : teacherid;
        const targetAssignments = targetTeacherId
          ? await getClassesByTeacherId(targetTeacherId)
          : [];
        const targetAssociatedClassIds = getAssociatedClassIds(targetAssignments);
        const classOwnerName = isOtherTeacherMode
          ? activePrefillContext.secondTeacherName
          : activePrefillContext.teacherName;
        const requestableClasses = targetTeacherId
          ? normalisedClasses.filter((classItem) =>
              teacherNeedsClassAccess(classItem, targetTeacherId, targetAssociatedClassIds)
            )
          : normalisedClasses;
        const prefilledClass = getSingleExactClassMatch(requestableClasses, {
          className: activePrefillContext.className,
          ownerName: classOwnerName,
          targetTeacher: prefilledTeacher
        });

        if (cancelled) {
          return;
        }

        setTeachers(availableTeachers);
        setClasses(normalisedClasses);
        setSelectedTeacher(prefilledTeacher);
        setSelectedClass(prefilledClass);
        setAssociatedClassIds(targetAssociatedClassIds);
        setHasLoadedTargetAccess(Boolean(targetTeacherId));
      } catch (loadError) {
        if (cancelled) {
          return;
        }

        console.error("Error loading requestable classes:", loadError);
        setClasses([]);
        setError("Classes could not be loaded. Please try again.");
      } finally {
        if (cancelled) {
          return;
        }

        setIsLoading(false);
      }
    };

    loadRequestContext();

    return () => {
      cancelled = true;
      teacherAccessLoadId.current += 1;
    };
  }, [
    show,
    schoolid,
    countryGrades,
    teacherid,
    isOtherTeacherMode,
    prefillContext,
    restartCount
  ]);

  const filteredTeachers = useMemo(() => {
    const normalisedSearchTerm = trimmedTeacherSearchTerm.toLowerCase();

    if (!normalisedSearchTerm) {
      return [];
    }

    return teachers.filter((teacher) =>
      [teacher.name, teacher.roleLabel].some((value) =>
        String(value || "").toLowerCase().includes(normalisedSearchTerm)
      )
    );
  }, [teachers, trimmedTeacherSearchTerm]);

  const filteredClasses = useMemo(() => {
    const normalisedSearchTerm = trimmedSearchTerm.toLowerCase();

    if (!normalisedSearchTerm) {
      return [];
    }

    return classes.filter((classItem) => {
      const targetTeacherId = isOtherTeacherMode ? selectedTeacher?.id : teacherid;
      const targetTeacherNeedsAccess = !targetTeacherId || (
        hasLoadedTargetAccess &&
        teacherNeedsClassAccess(classItem, targetTeacherId, associatedClassIds)
      );

      return targetTeacherNeedsAccess && classItem.searchText.includes(normalisedSearchTerm);
    });
  }, [
    associatedClassIds,
    classes,
    hasLoadedTargetAccess,
    isOtherTeacherMode,
    selectedTeacher,
    teacherid,
    trimmedSearchTerm
  ]);

  const handleSelectTeacher = async (teacher) => {
    const loadId = teacherAccessLoadId.current + 1;
    teacherAccessLoadId.current = loadId;
    setSelectedTeacher(teacher);
    setSelectedClass(null);
    setTeacherSearchTerm(teacher.name);
    setError("");
    setAssociatedClassIds(new Set());
    setHasLoadedTargetAccess(false);
    setIsLoadingTeacherAccess(true);

    try {
      const assignments = await getClassesByTeacherId(teacher.id);

      if (teacherAccessLoadId.current !== loadId) {
        return;
      }

      setAssociatedClassIds(getAssociatedClassIds(assignments));
      setHasLoadedTargetAccess(true);
    } catch (loadError) {
      if (teacherAccessLoadId.current !== loadId) {
        return;
      }

      console.error("Error loading teacher class access:", loadError);
      setError("Classes could not be loaded. Please try again.");
    } finally {
      if (teacherAccessLoadId.current === loadId) {
        setIsLoadingTeacherAccess(false);
      }
    }
  };

  const handleClearTeacher = () => {
    teacherAccessLoadId.current += 1;
    setSelectedTeacher(null);
    setSelectedClass(null);
    setAssociatedClassIds(new Set());
    setHasLoadedTargetAccess(false);
    setIsLoadingTeacherAccess(false);
  };

  const handleSelectClass = (classItem) => {
    setSelectedClass(classItem);
    setSearchTerm(classItem.name);
    setError("");
  };

  const handleSendRequest = async () => {
    if (!canSend) {
      return;
    }

    setIsSending(true);
    setError("");

    try {
      const targetTeacherId = isOtherTeacherMode ? selectedTeacher.id : teacherid;

      if (hasPlaceholderTargetId({ classId: selectedClass.id, targetTeacherId })) {
        throw new Error("Missing real class or teacher id.");
      }

      const response = await createClassAccessRequest(schoolid, selectedClass.id, {
        targetTeacherId,
        note: note.trim() || undefined
      });
      setIsDuplicatePending(isDuplicatePendingResponse(response));
      setStage("success");
    } catch (sendError) {
      console.error("Error sending class access request:", sendError);
      setError(getSupportRequestErrorMessage(sendError));
    } finally {
      setIsSending(false);
    }
  };

  const handleRequestAnotherClass = () => {
    teacherAccessLoadId.current += 1;
    setStage("form");
    setSearchTerm("");
    setTeacherSearchTerm("");
    setSelectedTeacher(null);
    setSelectedClass(null);
    setNote("");
    setError("");
    setIsDuplicatePending(false);
    setAssociatedClassIds(new Set());
    setHasLoadedTargetAccess(false);
    setRestartCount((currentCount) => currentCount + 1);
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        message: isOtherTeacherMode ? "Teacher access request sent." : "Access request sent."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const handleOpenOppositeFlow = () => {
    if (!onOpenOppositeFlow) {
      return;
    }

    onOpenOppositeFlow({
      className: selectedClass?.name,
      teacherName: isOtherTeacherMode ? selectedTeacher?.name : "",
      secondTeacherName: selectedClass?.ownerName
    });
  };

  const renderTeacherResults = () => {
    if (isLoading) {
      return (
        <div className="request-access-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading teachers...</span>
        </div>
      );
    }

    if (selectedTeacher) {
      return (
        <button
          key={selectedTeacher.id}
          type="button"
          className="request-access-result selected"
          onClick={handleClearTeacher}
          aria-label={`Select ${selectedTeacher.name}${selectedTeacher.roleLabel ? ` - ${selectedTeacher.roleLabel}` : ""}`}
          aria-pressed
        >
          <span className="request-access-radio" aria-hidden="true" />
          <span className="request-access-result-copy">
            <strong>{selectedTeacher.name}</strong>
            {selectedTeacher.roleLabel && <span>{selectedTeacher.roleLabel}</span>}
          </span>
        </button>
      );
    }

    if (!trimmedTeacherSearchTerm) {
      return (
        <p className="request-access-muted">
          Start with the teacher's name.
        </p>
      );
    }

    if (filteredTeachers.length < 1) {
      return (
        <div className="request-access-empty-result">
          <strong>No matching teachers found.</strong>
          <span>Try the teacher's name again.</span>
        </div>
      );
    }

    return filteredTeachers.map((teacher) => {
      const isSelected = selectedTeacher?.id === teacher.id;

      return (
        <button
          key={teacher.id}
          type="button"
          className={`request-access-result${isSelected ? " selected" : ""}`}
          onClick={() => handleSelectTeacher(teacher)}
          aria-label={`Select ${teacher.name}${teacher.roleLabel ? ` - ${teacher.roleLabel}` : ""}`}
          aria-pressed={isSelected}
        >
          <span className="request-access-radio" aria-hidden="true" />
          <span className="request-access-result-copy">
            <strong>{teacher.name}</strong>
            {teacher.roleLabel && <span>{teacher.roleLabel}</span>}
          </span>
        </button>
      );
    });
  };

  const renderResults = () => {
    if (isLoading || isLoadingTeacherAccess) {
      return (
        <div className="request-access-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading classes...</span>
        </div>
      );
    }

    if (selectedClass) {
      return (
        <button
          key={selectedClass.id}
          type="button"
          className="request-access-result selected"
          onClick={() => setSelectedClass(null)}
          aria-label={`Select ${selectedClass.name}${selectedClass.detailLabel ? ` - ${selectedClass.detailLabel}` : ""}`}
          aria-pressed
        >
          <span className="request-access-radio" aria-hidden="true" />
          <span className="request-access-result-copy">
            <strong>{selectedClass.name}</strong>
            {selectedClass.detailLabel && <span>{selectedClass.detailLabel}</span>}
          </span>
        </button>
      );
    }

    if (!trimmedSearchTerm) {
      return (
        <p className="request-access-muted">
          Start with the teacher's name or class name.
        </p>
      );
    }

    if (filteredClasses.length < 1) {
      return (
        <div className="request-access-empty-result">
          <strong>No matching classes found.</strong>
          <span>Try the teacher's name or class name.</span>
        </div>
      );
    }

    return filteredClasses.map((classItem) => {
      const isSelected = selectedClass?.id === classItem.id;

      return (
        <button
          key={classItem.id}
          type="button"
          className={`request-access-result${isSelected ? " selected" : ""}`}
          onClick={() => handleSelectClass(classItem)}
          aria-label={`Select ${classItem.name}${classItem.detailLabel ? ` - ${classItem.detailLabel}` : ""}`}
          aria-pressed={isSelected}
        >
          <span className="request-access-radio" aria-hidden="true" />
          <span className="request-access-result-copy">
            <strong>{classItem.name}</strong>
            {classItem.detailLabel && <span>{classItem.detailLabel}</span>}
          </span>
        </button>
      );
    });
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>{isOtherTeacherMode ? "Request Access For Teacher" : "Request Access"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!isOtherTeacherMode && (
          <p className="request-access-intro">I need access to a class.</p>
        )}
        {error && <Alert variant="danger">{error}</Alert>}

        {isOtherTeacherMode && (
          <div className="request-access-section">
            <Form.Group controlId="teacherRequestAccessTeacherSearch">
              <Form.Label>Which teacher needs access?</Form.Label>
              <Form.Text as="div" className="request-access-field-label">
                Search teacher
              </Form.Text>
              <Form.Control
                type="search"
                value={teacherSearchTerm}
                onChange={(event) => {
                  teacherAccessLoadId.current += 1;
                  setTeacherSearchTerm(event.target.value);
                  setSelectedTeacher(null);
                  setSelectedClass(null);
                  setAssociatedClassIds(new Set());
                  setHasLoadedTargetAccess(false);
                  setIsLoadingTeacherAccess(false);
                  setError("");
                }}
                placeholder="Teacher name"
                aria-label="Search teacher"
              />
            </Form.Group>

            <div className="request-access-results" aria-label="Matching teachers">
              {renderTeacherResults()}
            </div>
          </div>
        )}

        <Form.Group className={isOtherTeacherMode ? "request-access-section" : ""} controlId="teacherRequestAccessSearch">
          <Form.Label>
            {isOtherTeacherMode ? "Which class do they need?" : "Which class do you need?"}
          </Form.Label>
          <Form.Text as="div" className="request-access-field-label">
            Search by teacher or class
          </Form.Text>
          <Form.Control
            type="search"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setSelectedClass(null);
              setError("");
            }}
            placeholder="Teacher or class name"
            aria-label="Search by teacher or class"
          />
        </Form.Group>

        <div className="request-access-results" aria-label="Matching classes">
          {renderResults()}
        </div>

        <Form.Group className="mt-4" controlId="teacherRequestAccessNote">
          <Form.Label>Optional note</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            aria-label="Optional note"
          />
          <Form.Text className="text-muted">
            Add a short note only if it helps explain the request.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <span className="request-access-footer-note">
          {isOtherTeacherMode
            ? selectedTeacher && selectedClass
              ? `${selectedTeacher.name} and ${selectedClass.name} selected.`
              : selectedTeacher
                ? "Class not selected."
                : "Teacher not selected."
            : selectedClass
              ? `${selectedClass.name} selected.`
              : "No class selected."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={!canSend} onClick={handleSendRequest}>
          {isSending ? "Sending..." : "Send request"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Request Sent</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!isOtherTeacherMode && (
          <p className="request-access-intro">Your class list has not changed yet.</p>
        )}
        <Alert variant="success" className="request-access-success">
          <FaCheckCircle aria-hidden="true" />
          {isOtherTeacherMode ? (
            <span>
              <strong>
                {isDuplicatePending
                  ? `${selectedTeacher?.name}'s access request is already in progress.`
                  : `${selectedTeacher?.name} will be able to access ${selectedClass?.name} once the request is approved.`}
              </strong>
            </span>
          ) : (
            <span>
              <strong>
                {isDuplicatePending
                  ? `Your request for ${selectedClass?.name} is already in progress.`
                  : `Your request for ${selectedClass?.name} has been sent.`}
              </strong>
              <span>
                Your class list will stay the same until the request is approved.
              </span>
            </span>
          )}
        </Alert>
        <div className="request-access-next">
          <strong>What next?</strong>
          <span>
            {isOtherTeacherMode
              ? `${embedded ? "Done returns to SENSAI" : "Done returns to Help Centre home"}. You can also send another request.`
              : `${embedded ? "Done returns to SENSAI" : "Done returns to Help Centre home"}. You can also request another class.`}
          </span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="request-access-footer-note">Request sent.</span>
        <Button variant="outline-secondary" onClick={handleRequestAnotherClass}>
          {isOtherTeacherMode ? "Send another request" : "Request another class"}
        </Button>
        {onOpenOppositeFlow && (
          <Button variant="outline-secondary" onClick={handleOpenOppositeFlow}>
            {isOtherTeacherMode ? "Request access for myself" : "Request access for a teacher"}
          </Button>
        )}
        <Button variant="primary" onClick={handleDone}>
          Done
        </Button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-request-access-modal"
    >
      {stage === "form" && renderForm()}
      {stage === "success" && renderSuccess()}
    </Modal>
  );
}

export default TeacherRequestAccessModal;
